import { useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import {
  Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { DollarSign, HardHat, Sparkles, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { ChartCard, DataGrid, KpiCard, MetricStrip } from "@/components/dashboard";
import { formatCurrency, formatShortDate } from "@/lib/format";
import WorkerDetailSheet from "./worker-detail-sheet";

export type WorkerSummaryRow = {
  userId: number;
  name: string;
  jobs: number;
  pendingJobs: number;
  completedJobs: number;
  totalPayout: string;
  pendingPayout: string;
  estimatedPayout: string;
};

type CleaningJobRow = {
  id: number;
  apartmentName?: string;
  apartmentId?: number;
  assignedToName?: string;
  assignedToUserId?: number;
  status: string;
  priority: string;
  scheduledDate?: string | null;
  createdAt?: string | null;
  cost?: string | number | null;
};

type MaintenanceJobRow = {
  id: number;
  apartmentName?: string;
  apartmentId?: number;
  description?: string;
  status: string;
  createdAt?: string | null;
  cost?: string | number | null;
};

function dailyBuckets(records: Array<{ date?: string | null; value?: number }>, days = 30): { date: string; value: number }[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const buckets: Record<string, number> = {};
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    buckets[d.toISOString().slice(0, 10)] = 0;
  }
  for (const r of records) {
    if (!r.date) continue;
    const key = new Date(r.date).toISOString().slice(0, 10);
    if (key in buckets) buckets[key] = (buckets[key] ?? 0) + (r.value ?? 1);
  }
  return Object.entries(buckets).map(([date, value]) => ({ date, value }));
}

function shortDay(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export type OpsReportData = {
  cleaningJobs: CleaningJobRow[];
  maintenanceJobs: MaintenanceJobRow[];
  workerSummary: WorkerSummaryRow[];
  summary: {
    totalCleaningJobs: number;
    completedCleaningJobs: number;
    pendingCleaningJobs: number;
    totalMaintenanceJobs: number;
    completedMaintenanceJobs: number;
    totalCleaningRevenue: string;
    totalMaintenanceCost: string;
    totalOperationsCost?: string;
  };
};

export type OperationsTabProps = {
  opsData?: OpsReportData;
  apartmentMap: Record<number, string>;
  userMap: Record<number, string>;
  opsStatusFilter: string;
  onOpsStatusChange: (value: string) => void;
};

function statusBadgeVariant(status: string): "default" | "secondary" | "outline" | "destructive" {
  if (status === "completed" || status === "paid") return "default";
  if (status === "in_progress" || status === "checked_in" || status === "confirmed") return "secondary";
  if (status === "cancelled" || status === "urgent") return "destructive";
  return "outline";
}

export function OperationsTab({ opsData, apartmentMap, userMap, opsStatusFilter, onOpsStatusChange }: OperationsTabProps) {
  const summary = opsData?.summary;
  const cleaningJobs = opsData?.cleaningJobs ?? [];
  const maintenanceJobs = opsData?.maintenanceJobs ?? [];
  const workerSummary = opsData?.workerSummary ?? [];
  const [selectedWorker, setSelectedWorker] = useState<WorkerSummaryRow | null>(null);

  // ---- Time-series buckets for sparklines + trend chart ----
  const cleaningSeries = useMemo(
    () => dailyBuckets(cleaningJobs.map(j => ({ date: j.scheduledDate ?? j.createdAt }))),
    [cleaningJobs],
  );
  const maintenanceSeries = useMemo(
    () => dailyBuckets(maintenanceJobs.map(j => ({ date: j.createdAt }))),
    [maintenanceJobs],
  );
  const costSeries = useMemo(() => {
    const records = [
      ...cleaningJobs.map(j => ({ date: j.scheduledDate ?? j.createdAt, value: Number(j.cost ?? 0) })),
      ...maintenanceJobs.map(j => ({ date: j.createdAt, value: Number(j.cost ?? 0) })),
    ];
    return dailyBuckets(records);
  }, [cleaningJobs, maintenanceJobs]);

  const trendData = useMemo(() => cleaningSeries.map((c, i) => ({
    date: c.date,
    label: shortDay(c.date),
    cleaning: c.value,
    maintenance: maintenanceSeries[i]?.value ?? 0,
  })), [cleaningSeries, maintenanceSeries]);

  const cleaningSparkline = cleaningSeries.map(b => b.value);
  const maintenanceSparkline = maintenanceSeries.map(b => b.value);
  const costSparkline = costSeries.map(b => b.value);

  const cleaningColumns = useMemo<ColumnDef<CleaningJobRow, unknown>[]>(() => [
    {
      accessorFn: row => row.apartmentName || apartmentMap[Number(row.apartmentId)] || "-",
      id: "apartment",
      header: "Apartment",
      cell: info => <span className="font-medium">{String(info.getValue())}</span>,
    },
    {
      accessorFn: row => row.assignedToName || userMap[Number(row.assignedToUserId)] || "-",
      id: "assignedTo",
      header: "Assigned To",
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: info => <Badge variant={statusBadgeVariant(String(info.getValue()))}>{String(info.getValue())}</Badge>,
    },
    {
      accessorKey: "priority",
      header: "Priority",
      cell: info => {
        const p = String(info.getValue());
        return <Badge variant={p === "urgent" ? "destructive" : "outline"}>{p}</Badge>;
      },
    },
    {
      accessorKey: "scheduledDate",
      header: "Date",
      cell: info => {
        const v = info.getValue() as string | null | undefined;
        return v ? formatShortDate(v) : "-";
      },
    },
  ], [apartmentMap, userMap]);

  const workerColumns = useMemo<ColumnDef<WorkerSummaryRow, unknown>[]>(() => [
    { accessorKey: "name", header: "Worker", cell: info => <span className="font-medium">{String(info.getValue())}</span> },
    { accessorKey: "jobs", header: "Jobs" },
    { accessorKey: "completedJobs", header: "Completed" },
    { accessorKey: "pendingJobs", header: "Pending" },
    {
      accessorKey: "totalPayout",
      header: "Paid/Earned",
      cell: info => <span className="font-medium">{formatCurrency(Number(info.getValue()))}</span>,
    },
    {
      accessorKey: "pendingPayout",
      header: "Awaiting Payment",
      cell: info => <span className="text-orange-600">{formatCurrency(Number(info.getValue()))}</span>,
    },
    {
      accessorKey: "estimatedPayout",
      header: "Est. Pending",
      cell: info => <span className="text-blue-600 font-medium">{formatCurrency(Number(info.getValue() ?? 0))}</span>,
    },
  ], []);

  const maintenanceColumns = useMemo<ColumnDef<MaintenanceJobRow, unknown>[]>(() => [
    {
      accessorFn: row => row.apartmentName || apartmentMap[Number(row.apartmentId)] || "-",
      id: "apartment",
      header: "Apartment",
      cell: info => <span className="font-medium">{String(info.getValue())}</span>,
    },
    { accessorKey: "description", header: "Description", cell: info => String(info.getValue() ?? "-") },
    {
      accessorKey: "status",
      header: "Status",
      cell: info => <Badge variant={statusBadgeVariant(String(info.getValue()))}>{String(info.getValue())}</Badge>,
    },
    {
      accessorKey: "createdAt",
      header: "Date",
      cell: info => {
        const v = info.getValue() as string | null | undefined;
        return v ? formatShortDate(v) : "-";
      },
    },
  ], [apartmentMap]);

  const workerTotals = workerSummary.reduce(
    (acc, w) => ({
      jobs: acc.jobs + w.jobs,
      completed: acc.completed + w.completedJobs,
      pending: acc.pending + w.pendingJobs,
      paid: acc.paid + Number(w.totalPayout),
      awaiting: acc.awaiting + Number(w.pendingPayout),
      estimated: acc.estimated + Number(w.estimatedPayout ?? 0),
    }),
    { jobs: 0, completed: 0, pending: 0, paid: 0, awaiting: 0, estimated: 0 },
  );

  return (
    <div className="space-y-4">
      <MetricStrip cols={3}>
        <KpiCard
          label="Cleaning Jobs"
          value={summary?.totalCleaningJobs ?? 0}
          helper={`${summary?.completedCleaningJobs ?? 0} completed · ${summary?.pendingCleaningJobs ?? 0} pending`}
          icon={Sparkles}
          variant="info"
          sparkline={cleaningSparkline}
          testId="stat-ops-cleaning"
        />
        <KpiCard
          label="Maintenance Jobs"
          value={summary?.totalMaintenanceJobs ?? 0}
          helper={`${summary?.completedMaintenanceJobs ?? 0} completed`}
          icon={HardHat}
          variant="warning"
          sparkline={maintenanceSparkline}
          testId="stat-ops-maintenance"
        />
        <KpiCard
          label="Operations Cost"
          value={formatCurrency(Number(summary?.totalOperationsCost ?? 0))}
          helper="cleaning + maintenance"
          icon={DollarSign}
          variant="success"
          sparkline={costSparkline}
          testId="stat-ops-cost"
        />
      </MetricStrip>

      <ChartCard
        title="Jobs over time"
        description="Daily count for the last 30 days"
        height={220}
        isEmpty={trendData.every(d => d.cleaning === 0 && d.maintenance === 0)}
        emptyText="No jobs in the last 30 days"
        testId="card-ops-trend"
      >
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={trendData} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id="ops-cleaning" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.45} />
                <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="ops-maint" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.45} />
                <stop offset="100%" stopColor="#f59e0b" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 10 }} width={28} allowDecimals={false} />
            <Tooltip
              contentStyle={{ fontSize: 12, borderRadius: 8 }}
              labelFormatter={l => l}
            />
            <Area type="monotone" dataKey="cleaning" name="Cleaning" stroke="#3b82f6" fill="url(#ops-cleaning)" strokeWidth={2} />
            <Area type="monotone" dataKey="maintenance" name="Maintenance" stroke="#f59e0b" fill="url(#ops-maint)" strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </ChartCard>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2 flex-wrap">
            <Sparkles className="h-4 w-4 text-muted-foreground" />
            Cleaning Jobs
          </CardTitle>
          <div className="flex items-center gap-2 pt-1">
            <Label className="text-xs text-muted-foreground">Show:</Label>
            <Select value={opsStatusFilter} onValueChange={onOpsStatusChange}>
              <SelectTrigger className="h-7 w-36 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All jobs</SelectItem>
                <SelectItem value="completed">Completed only</SelectItem>
                <SelectItem value="pending">Pending only</SelectItem>
                <SelectItem value="in_progress">In progress only</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Separator className="mt-3" />
        </CardHeader>
        <CardContent className="px-0 pb-2">
          <DataGrid
            data={cleaningJobs}
            columns={cleaningColumns}
            searchPlaceholder="Search cleaning jobs…"
            initialDensity="compact"
            showDensityToggle={false}
            exportFilename="ops-cleaning-jobs"
            getRowId={r => String(r.id)}
            emptyText="No cleaning jobs in period"
            className="border-0 shadow-none rounded-none"
          />
          {cleaningJobs.length > 0 && (
            <p className="px-4 pb-3 pt-2 text-xs text-muted-foreground border-t">
              Total: {cleaningJobs.length} jobs ({summary?.completedCleaningJobs ?? 0} completed,{" "}
              {summary?.pendingCleaningJobs ?? 0} pending/in-progress)
            </p>
          )}
        </CardContent>
      </Card>

      {workerSummary.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              Worker Payment Summary
            </CardTitle>
            <Separator className="mt-3" />
          </CardHeader>
          <CardContent className="px-0 pb-2">
            <DataGrid
              data={workerSummary}
              columns={workerColumns}
              searchPlaceholder="Search worker…"
              initialDensity="compact"
              showDensityToggle={false}
              exportFilename="worker-payment-summary"
              getRowId={r => String(r.userId)}
              emptyText="No worker payments in period"
              className="border-0 shadow-none rounded-none"
              onRowClick={row => setSelectedWorker(row)}
            />
            <div className="px-4 pt-2 pb-3 border-t flex flex-wrap gap-x-6 gap-y-1 text-xs">
              <span className="font-semibold">
                Total: {workerTotals.jobs} jobs
              </span>
              <span className="text-muted-foreground">
                {workerTotals.completed} completed · {workerTotals.pending} pending
              </span>
              <span className="font-medium">Paid: {formatCurrency(workerTotals.paid)}</span>
              <span className="text-orange-600 font-medium">
                Awaiting: {formatCurrency(workerTotals.awaiting)}
              </span>
              <span className="text-blue-600 font-medium">
                Est. pending: {formatCurrency(workerTotals.estimated)}
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      <WorkerDetailSheet
        worker={selectedWorker}
        cleaningJobs={cleaningJobs}
        apartmentMap={apartmentMap}
        onClose={() => setSelectedWorker(null)}
      />

      {maintenanceJobs.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2 flex-wrap">
              <HardHat className="h-4 w-4 text-muted-foreground" />
              Maintenance Jobs
            </CardTitle>
            <Separator className="mt-3" />
          </CardHeader>
          <CardContent className="px-0 pb-2">
            <DataGrid
              data={maintenanceJobs}
              columns={maintenanceColumns}
              searchPlaceholder="Search maintenance…"
              initialDensity="compact"
              showDensityToggle={false}
              exportFilename="ops-maintenance-jobs"
              getRowId={r => String(r.id)}
              emptyText="No maintenance jobs in period"
              className="border-0 shadow-none rounded-none"
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default OperationsTab;
